import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { saleCloseoutService } from "../services/service/saleCloseoutService";

const EMPTY_PARAMS = {};
const SALE_CLOSEOUTS_KEY = ["sale-closeouts"];

export const useShopSaleCloseouts = (
    shopId,
    initialParams = EMPTY_PARAMS
) => {
    const queryClient = useQueryClient();

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedSeller, setSelectedSeller] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const filters = useMemo(() => ({
        search: searchTerm || undefined,
        shopSellerId:
            selectedSeller !== "all"
                ? selectedSeller
                : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...initialParams,
    }), [
        searchTerm,
        selectedSeller,
        startDate,
        endDate,
        initialParams,
    ]);

    const params = useMemo(() => ({
        page: currentPage,
        per_page: pageSize,
        ...filters,
    }), [currentPage, pageSize, filters]);

    const query = useQuery({
        queryKey: [
            ...SALE_CLOSEOUTS_KEY,
            "shop",
            shopId,
            params
        ],
        queryFn: () =>
            saleCloseoutService.getByShop(
                shopId,
                params
            ),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const totalQuery = useQuery({
        queryKey: [
            ...SALE_CLOSEOUTS_KEY,
            "shop-total",
            shopId,
            filters
        ],
        queryFn: () =>
            saleCloseoutService.getTotalByShop(
                shopId,
                filters
            ),
        enabled: Boolean(shopId),
        staleTime: Infinity,
    });

    const invalidate = useCallback(() => {
        return queryClient.invalidateQueries({
            queryKey: SALE_CLOSEOUTS_KEY
        });
    }, [queryClient]);

    const createSaleCloseout = useCallback(
        async (data) => {
            const response =
                await saleCloseoutService.create(data);

            await invalidate();

            return response;
        },
        [invalidate]
    );

    const updateSaleCloseout = useCallback(
        async (closeoutId, data) => {
            const response =
                await saleCloseoutService.update(
                    closeoutId,
                    data
                );

            await invalidate();

            return response;
        },
        [invalidate]
    );

    return {
        saleCloseouts:
            query.data?.data?.data ||
            query.data?.data ||
            [],

        pagination:
            query.data?.data || null,

        totals: (() => {
            const raw = totalQuery.data;
            if (!raw) return null;

            const t = raw?.data ?? raw;

            return {
                totalAmount: Number(
                    t?.total_closeout ??
                    t?.totalAmount ??
                    t?.total_amount ??
                    t?.amount ??
                    t?.total ??
                    0
                ),
                totalCloseouts: Number(
                    t?.count ??
                    t?.totalCloseouts ??
                    t?.total_closeouts ??
                    t?.total ??
                    0
                ),
            };
        })(),

        loading:
            query.isLoading ||
            query.isFetching ||
            totalQuery.isLoading ||
            totalQuery.isFetching,

        error:
            query.error ||
            totalQuery.error,

        searchTerm,
        setSearchTerm,

        selectedSeller,
        setSelectedSeller,

        currentPage,
        setCurrentPage,

        pageSize,
        setPageSize,

        startDate,
        setStartDate,

        endDate,
        setEndDate,

        refresh:
            query.refetch,

        refreshTotals:
            totalQuery.refetch,

        createSaleCloseout,
        updateSaleCloseout,
    };
};

export const useSaleCloseouts = useShopSaleCloseouts;

export const useSaleCloseout = useShopSaleCloseouts;